'use strict';

import {
    addObjectToLocalStorage, addFloorOrdinalSuffix,
    findSelectionAndCrmData, getFieldsFromAPI,
    getObjectFromLocalStorage,
    getTowerName,
    isCompareMode,
    isNumeric,
    moveAroundTheFloors,
    numberWithCommas,
    removeObjectFromLocalStorage,
    userPrevent,
} from '../../../functions';
import {
    sortArray, toggleClassFavoritesInSearch
} from './search';
import { intersectionClickHandler } from '../../threejs/raycast';
import { openOptionsCard, setWidthOfCardDependingOnMenu } from '../../cards/optionsCards';
import {
    changeLangToCurrent,
    getCurrentLang,
    getLanguagesTranslations,
} from '../../components/langSwitcher/langSwitcher';
import { dataArrayOfFlat, facingDictionary } from '../../cards/cardsConfig';
import {CardTypes} from "../../../common";
import {analytics} from "../../analytics/analytics";
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import {
    Buildings,
    cardsData,
    isMobile,
    projectMode,
    settingsData
} from '../../../settings';
import {addAdditionalButtons, getPropType} from "../../cards/unitCards/unitCardFunctions";
import {BaseElement} from "../../interfaces/BaseElement";
import { Vantages } from '../../components/vantage/Vantage';
import { Price } from '../../cards/Price';

export function findSimilarApartments (optionCardContainer = document.querySelector(`.${CardTypes.Options}-card__data`) ) {
    const result = {};
    const totalResultInOneBuilding = 5;

    const clickedApt = settingsData.lastClickedFlat.userData.crm_data;
    const price = clickedApt.salePrice;
    const roomNumber = clickedApt.roomNum;
    const exposure = clickedApt.facing;

    if (price === 0){
        Object.values(Buildings.buildings).forEach((building) =>{
           if (!building.getInfo())
               return;

           let filteredApts = building.info.crm_data.filter(apt =>
               apt.status === 'Available' &&
               apt.roomNum === roomNumber &&
               apt.facing.toString() === exposure.toString() &&
               apt !== clickedApt
           );

           filteredApts = sortArray('minmax', 'salePrice', filteredApts);

            if (filteredApts.length > totalResultInOneBuilding)
                filteredApts.length = totalResultInOneBuilding;

            const towerName = getTowerName(building);
            result[towerName] = filteredApts;
        });
    } else {
        const minPrice = price;
        const maxPrice = price * 1.2;

        Object.values(Buildings.buildings).forEach((building) =>{
            if (!building.getInfo())
                return;

            let filteredApts = building.info.crm_data.filter(apt =>
                apt.status === 'Available' &&
                (apt.salePrice >= minPrice && apt.salePrice <= maxPrice) &&
                apt !== clickedApt
            );

            filteredApts = sortArray('minmax', 'salePrice', filteredApts);

            if (filteredApts.length > totalResultInOneBuilding)
                filteredApts.length = totalResultInOneBuilding;

            const towerName = getTowerName(building);
            result[towerName] = filteredApts;
        });
    }

    if (!checkResult(result) || isCompareMode())
        return;

    const croppedResult = makeAmountOfResultsEqual(result, 10);

    const similarContainer = new BaseElement()
        .addClass('similar__main__container')
        .appendTo(optionCardContainer);
    createSimilarAptCarousel(croppedResult, similarContainer.DOMElement);
}

function makeAmountOfResultsEqual(obj, limit) {
    // Convert the object into an array of [key, value] pairs
    const entries = Object.entries(obj);
    let totalLength = 0;
    const resultEntries = [];

    // Iterate through entries to calculate the total length and populate resultEntries
    for (const [key, array] of entries) {
        if (totalLength + array.length > limit) {
            // If the total length exceeds the limit, we need to start trimming from the end
            let remaining = limit - totalLength;

            // Check if we can add a part of the current array to the result
            if (remaining > 0) {
                resultEntries.push([key, array.slice(0, remaining)]);
                totalLength += remaining; // Adjust totalLength to include the partial array
            }
            break; // Stop the loop as we've reached the limit
        } else {
            // Add the array to the result if it doesn't exceed the limit
            resultEntries.push([key, array]);
            totalLength += array.length;
        }
    }

    // Convert the resultEntries back into an object
    return Object.fromEntries(resultEntries);
}

const createSimilarAptCarousel = (result, optionCardContainer) => {
    if (cardsData.cardMode.isAmenities)
        return;

    let showTitle = checkResult(result);
    showTitle && addTitle(optionCardContainer);
    const similarAptBlock = new BaseElement()
      .addClass(`${CardTypes.Similar}-card`, 'swiper')
      .appendTo(optionCardContainer);
    const similarAptBlockWrapper = new BaseElement()
        .addClass(`${CardTypes.Similar}-card__wrapper`, 'swiper-wrapper')
        .appendTo(similarAptBlock.DOMElement);

    Object.entries(result).forEach(([buildingName,apartments]) =>{
        apartments.forEach(apt =>{
            createCard(apt, similarAptBlockWrapper.DOMElement, buildingName);
        });

    });

    if (isMobile) {
        // createElements('div', ['swiper-pagination','similar-pagination'], similarAptBlock.DOMElement);
        new BaseElement()
            .addClass('swiper-pagination','similar-pagination')
            .appendTo(similarAptBlock.DOMElement);

        Swiper.use([Pagination]);
        new Swiper(`.${CardTypes.Similar}-card`, {
                direction: "horizontal",
                loop: false,
                slidesPerView: 1.05,
                spaceBetween: 5,
                pagination: {
                    el: '.similar-pagination',
                    type: 'bullets',
                },
            }
        );
    } else {
        const showButtons = checkResult(result);
        showButtons && addSwitchButtons(optionCardContainer, similarAptBlock.DOMElement);

        Swiper.use([Navigation]);
        new Swiper(`.${CardTypes.Similar}-card`, {
                direction: "horizontal",
                loop: false,
                slidesPerView: 1.3,
                spaceBetween: 20,
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                },
            }
        );
    }
}

const checkResult = (result) => {
    for (const res of Object.values(result)) {
        if (res.length) {
            return true;
        }
    }
}

/**
 * CARDS CONSTRUCTOR
 */

const createCard = (crmDataObject, similarAptBlock, buildingName) => {
    const similarAptContainer = new BaseElement()
        .addClass(`${CardTypes.Similar}-card__container`, 'swiper-slide')
        .addAttribute('data-engineid', crmDataObject.engineId)
        .appendTo(similarAptBlock, 'beforeend');

    if (!isMobile && crmDataObject.selectedProperty) {
        new BaseElement('span')
            .addClass(`${CardTypes.Similar}-card__label`, 'card__label', 'selected-property')
            .addAttribute('data-dictionary', 'selectedProperty')
            .innerHtml(changeLangToCurrent('selectedProperty'))
            .appendTo(similarAptContainer);
    }
    createHeader(crmDataObject, similarAptContainer.DOMElement, buildingName);
    createInfoPoints(crmDataObject, similarAptContainer.DOMElement);

    similarAptContainer.event('click', cardHandler);
    addAdditionalButtons({card: similarAptContainer.DOMElement, data: crmDataObject, cardType: CardTypes.Similar});
}

const createHeader = (crmDataObject, similarAptContainer, towerName) => {
    const similarAptHeader = new BaseElement()
      .addClass(`${CardTypes.Similar}-card__header`, 'card__header')
      .appendTo(similarAptContainer);
    const similarAptTitleBox = new BaseElement()
      .addClass(`${CardTypes.Similar}-card__title-box`, 'card__title-box')
      .appendTo(similarAptHeader.DOMElement);
    const similarAptSubtitle = new BaseElement()
      .addClass(`${CardTypes.Similar}-card__subtitle-box`, 'card__subtitle-box')
      .appendTo(similarAptTitleBox.DOMElement);
    const similarAptTitle = new BaseElement('h3')
      .addClass(`${CardTypes.Similar}-card__title`, 'card__title')
      .appendTo(similarAptTitleBox.DOMElement);
    new BaseElement('span')
      .addClass(`${CardTypes.Similar}-card__name`, 'card__title-flat-name', 'language-string')
      .innerHtml(changeLangToCurrent(getPropType(crmDataObject.propType)))
      .addAttribute('data-dictionary', `${getPropType(crmDataObject.propType)}`)
      .appendTo(similarAptTitle.DOMElement, 'beforeend');
    new BaseElement('span')
      .addClass(`${CardTypes.Similar}-card__number`, 'card__title-flat-number')
      .innerHtml(crmDataObject.propNum)
      .appendTo(similarAptTitle.DOMElement);

    let similarAptTowerName = null;
    if (Buildings.getBuildingsInfoLength() > 1 /*&& !Buildings.isOneFloorBuilding(crmDataObject.buildingEngineId)*/) {
        similarAptTowerName = new BaseElement()
          .addClass(`${CardTypes.Similar}-card__tower-name`, 'card__tower-name')
          .appendTo(similarAptSubtitle.DOMElement);
        if (!b.isOneFloorBuilding(crmDataObject.buildingEngineId))
            new BaseElement()
              .addClass('subtitle-separator')
              .appendTo(similarAptSubtitle.DOMElement);
    }
    const similarAptFloorNum = new BaseElement()
      .addClass(`${CardTypes.Similar}-card__floor-number`, 'options-card__floor-number')
      .appendTo(similarAptSubtitle.DOMElement);
    if (!b.isOneFloorBuilding(crmDataObject.buildingEngineId)) {
        const isGroundFloor = settingsData.groundFloor.isActive && (parseInt(settingsData.groundFloor.floor) === parseInt(crmDataObject.floorNum));
        const floorElement = new BaseElement('span')
          .addClass(`${CardTypes.Similar}-card__floor-number-floor`, 'card__floor_floor', 'language-string')
          .innerHtml(changeLangToCurrent('at floor') + ' ')
          .appendTo(similarAptFloorNum.DOMElement, 'beforeend');
        if (isGroundFloor) {
            const fullLabel = settingsData.groundFloor.fullLabel;
            floorElement
              .addAttribute('data-dictionary', fullLabel)
              .innerHtml(changeLangToCurrent(fullLabel));
        } else {
            const floorNumber = new BaseElement('span')
              .addClass(`${CardTypes.Similar}-card__floor-number_num`,'language-string', 'card__floor-number_num', isGroundFloor ? 'hidden' : null)
              .innerHtml(crmDataObject.floorNum)
              .appendTo(similarAptFloorNum.DOMElement);
    
            const translationData = getLanguagesTranslations('floor');
            const currentLang = getCurrentLang();
    
            Object.entries(translationData).forEach(([lang,value]) => {
                let floorElementValue = value;
                let floorNumberValue = crmDataObject.floorNum;
        
                if (lang === 'en') {
                    floorElementValue = addFloorOrdinalSuffix(floorNumberValue);
                    floorNumberValue = ' ';
                }
        
                if (lang === currentLang) {
                    floorElement.innerHtml(floorElementValue);
                    floorNumber.innerHtml(floorNumberValue);
                }
        
                floorElement.addAttribute(`data-lang-${lang}`,floorElementValue);
                floorNumber.addAttribute(`data-lang-${lang}`,floorNumberValue);
            });
        }
    }

    const regExp = /[a-z\u0590-\u05fe]/gi;
    const isStringName = regExp.test(towerName);

    if (Buildings.getBuildingsInfoLength() > 1 /*&& !Buildings.isOneFloorBuilding(crmDataObject.buildingEngineId)*/) {
        if (!isStringName) {
            let towerNumber = similarAptSubtitle.DOMElement.querySelector(`.${CardTypes.Similar}-card__tower-name_num`);
            let towerTitle = similarAptSubtitle.DOMElement.querySelector(`.${CardTypes.Similar}-card__tower-name_title`);

            if (!towerNumber) {
                towerNumber = new BaseElement('span')
                    .addClass(`${CardTypes.Similar}-card__tower-name_num`, 'language-string')
                    .addAttribute('data-dictionary', 'building')
                    .appendTo(similarAptTowerName?.DOMElement, 'beforeend').DOMElement;

                towerTitle = new BaseElement('span')
                    .addClass(`${CardTypes.Similar}-card__tower-name_title`)
                    .appendTo(similarAptTowerName?.DOMElement).DOMElement;
            }

            towerTitle.innerHTML = towerName + ' ';
            towerNumber.innerHTML = changeLangToCurrent('building')  + ' ';
        } else {
            similarAptTowerName?.innerHtml(towerName);
        }
    }

    createPriceBlock(crmDataObject, similarAptHeader.DOMElement);
}

const createPriceBlock = (crmDataObject, similarAptTitleBox) => {
    const similarAptPrice = new BaseElement()
      .addClass(`${CardTypes.Similar}-card__price`, 'card__price')
      .appendTo(similarAptTitleBox);

    new Price()
        .addBlock(similarAptPrice.DOMElement)
        .setPrice(crmDataObject);
}

const createInfoPoints = (crmDataObject, similarAptContainer) => {
    const cardsInfo = dataArrayOfFlat(crmDataObject);
    const dictionary = facingDictionary();
    const infoPointsData = cardsInfo.filter(data => {
        if (getFieldsFromAPI.smallCard().indexOf(data.name) !== -1)
            return data;
    })

    const regExp = /([A-Za-z])\w+/g;
    const measurement = config.localization.area ? config.localization.area : "m2";
    const similarAptInfoPoints = new BaseElement()
        .addClass(`${CardTypes.Similar}-card__info-points`, 'card__info-points')
        .appendTo(similarAptContainer);

    infoPointsData.forEach(data => {
        if (parseInt(data.data, 10) === 0)
            return;

        let value = data.data;
        if (data.name === 'storages') {
            if (Array.isArray(value)) {
                if (value.length === 0)
                    return;

                const valueCopy = [ ...value ];
                value = 0;
                valueCopy.forEach(item => {
                    value += item.size;
                });
            }

            if (parseInt(value) === 0)
                return;
        }

        const cardContentWrapper = new BaseElement()
            .addClass(`${CardTypes.Similar}-card__info-points-wrapper`, 'card__info-points-block-wrapper')
            .appendTo(similarAptInfoPoints.DOMElement)
        const cardModelName = new BaseElement()
            .addClass(`${CardTypes.Similar}-card__info-points-block_data`, 'card__info-points-block_data')
            .appendTo(cardContentWrapper.DOMElement);
        const cardModelNameText = new BaseElement('span')
            .addClass(
                `${CardTypes.Similar}-card__info-points-block_data_text`,
                'card__info-points-block_data_text',
                'language-string')
            .addAttribute('data-dictionary', data.name === 'storages' ? value : data.data)
            .appendTo(cardModelName.DOMElement, 'beforeend');

        const contentSubtext = new BaseElement()
            .addClass(`${CardTypes.Similar}-card__info-points-block_subtitle`, 'card__info-points-block_subtitle')
            .appendTo(cardContentWrapper.DOMElement);

        if (regExp.test(data.data))
            cardModelNameText.addStyle('direction', 'ltr');

        contentSubtext.addInnerHtml(`<span class="language-string" data-dictionary="${data.name}">${changeLangToCurrent(data.name).toLowerCase()}</span>`);
        if (data.name === 'area' || data.name === 'balcony size') {
            const coma = `<span>,</span>`;
            contentSubtext.addInnerHtml(coma);

            new BaseElement('span')
                .addClass('language-string', 'card__info-points-block_subtitle-measurement', 'meters')
                .addInnerHtml(changeLangToCurrent(measurement))
                .addAttribute('data-dictionary', measurement)
                .appendTo(contentSubtext.DOMElement, 'beforeend')
        }

        if (isNumeric(data.data)) {
            cardModelNameText
                .innerHtml(numberWithCommas(data.data))
                .addClass('translate-number');
        } else {
            cardModelNameText
                .innerHtml(changeLangToCurrent(data.data))
                .addClass('language-string');
        }
        cardModelName.addClass(data.icon);

        if (data.name === 'exposure') {
            const dataArray = data.data;
            if (dataArray.length === 0) {
                cardContentWrapper.remove();
                return;
            }

            cardModelNameText.remove();

            dataArray.forEach(element => {
                for (let key in dictionary) {
                    if (element === key) {
                        new BaseElement('span')
                            .addClass(`${CardTypes.Similar}-card__info-points-block_data_text`, 'card__info-points-block_data_text', 'language-string')
                            .addAttribute('data-dictionary', dictionary[key].en + 'Short')
                            .innerHtml(changeLangToCurrent(dictionary[key].en + 'Short'))
                            .appendTo(cardModelName.DOMElement, 'beforeend');
                    }
                }
            });
        }

        if (data.name === 'parkings') {
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    cardContentWrapper.remove();
                    return;
                }

                value = value.length;
            }

            cardModelNameText.innerHtml(value);
        }

        if (data.name === 'storages') {
            cardModelNameText.innerHtml(value);
        }
    });
}

const addTitle = (optionCardContainer) => {
    new BaseElement('h4')
        .addClass('similar__text','language-string')
        .innerHtml(changeLangToCurrent('similarApartmentsTitle'))
        .addAttribute('data-dictionary','similarApartmentsTitle')
        .appendTo(optionCardContainer)
}

/**
 * CREATE SWITCH BLOCK
 */

const addSwitchButtons = (parent) => {
    // const switcherBlock = createElements('div', [`similar-switcher-block`, 'card__switcher-block'], parent);
    // createElements('button', ['icon-left1', 'swiper-button-prev'], switcherBlock);
    // createElements('button', ['icon-right1', 'swiper-button-next'], switcherBlock);
    const switcherBlock = new BaseElement()
        .addClass(`similar-switcher-block`, 'card__switcher-block')
        .appendTo(parent);
    new BaseElement('button')
        .addClass('icon-left1', 'swiper-button-prev')
        .appendTo(switcherBlock.DOMElement);
    new BaseElement('button')
        .addClass('icon-right1', 'swiper-button-next')
        .appendTo(switcherBlock.DOMElement);
}
/**
 * CLICK HANDLER
 */
const cardHandler = async (e) => {
    if (userPrevent())
        return;

    e.stopPropagation();

    const isTargetLabel = e.target.classList.contains(`${CardTypes.Similar}-card__favorite-label`);
    const isTargetCheckbox = e.target.classList.contains(`${CardTypes.Similar}-card__favorite-checkbox`);
    if (isTargetLabel || isTargetCheckbox)
        return;

    // let element = e.target.getAttribute('data-engineid');
    let element = e.target.closest(`.${CardTypes.Similar}-card__container`);
    element = element.getAttribute('data-engineid');

    Vantages.isActive() && await Vantages.close();

    const {selectedMesh} = findSelectionAndCrmData(element);
    
    if (projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty'))) {
        await intersectionClickHandler(selectedMesh);
        openOptionsCard( {
            object: selectedMesh.userData.crm_data,
            settings: config,
            params: {disableAnimation: true},
            isSimilarApartment: true
        });
        !isMobile && setWidthOfCardDependingOnMenu();
        return;
    }

    const buildingInfo = Buildings.getBuilding(element.split('_')[0]);
    const activeBuilding = Buildings.getActiveBuilding();
    if (buildingInfo.name === activeBuilding.name){
        if (settingsData.buildingDestroyed) {
            // settingsData.lastClickedFlat = selectedMesh;
            await moveAroundTheFloors(element)
        }
        await openSimilarCard(selectedMesh);
    } else {
        if (settingsData.buildingDestroyed){
            await projectMode.FloorPlan.exitFromMode();
            await projectMode.Cylinder.switchBuilding(buildingInfo.name);
        } else {
            await projectMode.Cylinder.switchBuilding(buildingInfo.name);
        }
        await openSimilarCard(selectedMesh);
    }
    settingsData.lastClickedFlat = selectedMesh;
};

async function openSimilarCard(selectedMesh) {
    await intersectionClickHandler(selectedMesh);
    setTimeout(() => {
        openOptionsCard( {
            object: selectedMesh.userData.crm_data,
            settings: config,
            params: {disableAnimation: true},
            isSimilarApartment: true
        });
    }, 100);
    !isMobile && setWidthOfCardDependingOnMenu();
}

/**
 * ADD/REMOVE FAVORITES
 */

export const similarFavoriteHandle = (e) => {
    let card, dataEngineID;
    let label;

    if (e.target.tagName === 'LORD-ICON'){
        label = e.target.parentElement;
    } else {
        label = e.target;
    }

    label.classList.toggle('icon-favorite_selected');
    label.classList.toggle('icon--lord-unselected');
    if (isMobile)
        label.classList.toggle('icon-unselected');

    card = e.target.closest(`.${CardTypes.Similar}-card__container`);
    dataEngineID = card.getAttribute('data-engineid');

    const {crmDataOfFlat} = findSelectionAndCrmData(dataEngineID);
    const storageData = getObjectFromLocalStorage(crmDataOfFlat.engineId);

    storageData ? removeObjectFromLocalStorage(crmDataOfFlat) : addObjectToLocalStorage(crmDataOfFlat);
    const target = findTargetOfSearch(dataEngineID);

    if (target)
        toggleClassFavoritesInSearch(target, storageData, true);

    analytics('favorites', {storageData, crmDataOfFlat});
}

const findTargetOfSearch = (dataEngineID) => {
    const searchList = document.querySelectorAll('.search__result-item');
    let targetLi, targetSpan;
    searchList.forEach(elem => {
        if (elem.getAttribute('data-engineid') === dataEngineID) {
            targetLi = elem;
        }
    });

    targetSpan = targetLi?.querySelector('span[data-favorite=favorite]');

    return targetSpan;
}

export const recreateBlockWhenLangSwitch = () => {
    const container = document.querySelector('.similar__main__container');
    if (container) {
        container.remove();
        findSimilarApartments();
    }
}
